// Tipos para Albarán (Delivery Note)

export interface DeliveryNoteCompany {
  name: string;
  address: string;
  city: string;
  province: string;
  postalCode: string;
  cif: string;
  phone: string;
  email?: string;
}

export interface DeliveryNoteCustomer {
  name: string;
  cif: string;
  address: string;
  city: string;
  province: string;
  postalCode?: string;
}

export interface DeliveryNoteItem {
  id: string;
  article: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  discount: number; // percentage
  subtotal: number;
  total: number;
}

export interface DeliveryNoteTax {
  type: number; // IVA percentage (e.g., 21)
  base: number;
  amount: number;
  discount: number;
  earlyPaymentDiscount: number;
}

export interface DeliveryNote {
  // Document info
  documentType: 'Albarán';
  documentNumber: string;
  page: number;
  totalPages: number;
  date: string;

  // Payment info
  paymentMethod: string;

  // Company info (MAGMA)
  company: DeliveryNoteCompany;

  // Customer info
  customer: DeliveryNoteCustomer;

  // Items
  items: DeliveryNoteItem[];

  // Taxes
  taxes: DeliveryNoteTax[];

  // Totals
  subtotal: number;
  totalDiscount: number;
  totalTax: number;
  total: number;

  // Observations
  observations: string;

  // Related work order
  workOrderId?: string;
  workOrderNumber?: string;

  // Output options
  hideValues?: boolean;
}

// Default company info for MAGMA COMPOSITES
export const DEFAULT_COMPANY: DeliveryNoteCompany = {
  name: 'MAGMA COMPOSITES S.L.',
  address: 'Pol. Ind. Cueva San José II, Nave nº8',
  city: 'CALANDA',
  province: 'TERUEL',
  postalCode: '44570',
  cif: 'ESB63222715',
  phone: '978877816',
  email: 'info@magmacomposites.com',
};

// Helper to generate document number
export function generateDocumentNumber(sequence: number): string {
  const year = new Date().getFullYear();
  const paddedSequence = String(sequence).padStart(6, '0');
  return `${year}-${paddedSequence}`;
}

// Helper to calculate item totals
export function calculateItemTotals(
  quantity: number,
  unitPrice: number,
  discount: number = 0
): { subtotal: number; total: number } {
  const subtotal = quantity * unitPrice;
  const discountAmount = subtotal * (discount / 100);
  const total = subtotal - discountAmount;
  return { subtotal, total };
}

// Helper to calculate tax
export function calculateTax(
  base: number,
  taxRate: number = 21,
  discount: number = 0,
  earlyPaymentDiscount: number = 0
): DeliveryNoteTax {
  const afterDiscounts = base - discount - earlyPaymentDiscount;
  const amount = afterDiscounts * (taxRate / 100);
  return {
    type: taxRate,
    base: afterDiscounts,
    amount,
    discount,
    earlyPaymentDiscount,
  };
}

// Helper to format currency
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}
